import { FF_REWARDS_CHINA } from 'constants/features';
import { REWARDS_ROUTE } from 'constants/routes';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags/usePeertransferFlags';
import { ReactComponent as StarIcon } from 'icons/ico_star_rewards.svg';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getRewardsCurrency,
  paymentsFromIndia as paymentsFromIndiaSelector,
} from 'selectors/ui/ui';
import { MobileDetector } from 'utils/mobileDetector/mobileDetector';
import { useTranslations } from 'utils/translations/useTranslations';

export const EARN_REWARDS_BUTTON_VARIANTS = {
  HEADER: 'header' as const,
  BANNER: 'banner' as const,
  MENU_MOBILE: 'menu-mobile' as const,
} as const;

export type EarnRewardsButtonVariant =
  | typeof EARN_REWARDS_BUTTON_VARIANTS.HEADER
  | typeof EARN_REWARDS_BUTTON_VARIANTS.BANNER
  | typeof EARN_REWARDS_BUTTON_VARIANTS.MENU_MOBILE;

interface EarnRewardsButtonProps {
  variant: EarnRewardsButtonVariant;
  onClick?: () => void;
}

export const EarnRewardsButton = ({
  variant,
  onClick,
}: EarnRewardsButtonProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const i18n = useTranslations();
  const paymentsFromIndia = useSelector(paymentsFromIndiaSelector);
  const rewardsCurrency = useSelector(getRewardsCurrency);

  const isMobile = MobileDetector.isMobile();
  const isRewardsPage = location.pathname === REWARDS_ROUTE;

  const { data: featureFlags } = usePeertransferFlags([FF_REWARDS_CHINA]);
  const isRewardChina =
    featureFlags?.[FF_REWARDS_CHINA as keyof typeof featureFlags];

  const hasValidRewardsForChina = isRewardChina && !!rewardsCurrency;
  const hasValidRewardsForIndia = !isRewardChina && paymentsFromIndia;

  const rewardsEnabled = hasValidRewardsForChina || hasValidRewardsForIndia;

  if (!rewardsEnabled || (isRewardsPage && !isMobile)) {
    return null;
  }

  const handleClick = () => {
    navigate(REWARDS_ROUTE);
    onClick?.();
  };

  if (variant === EARN_REWARDS_BUTTON_VARIANTS.BANNER) {
    return (
      <button
        onClick={handleClick}
        className="bg-gradient-to-r from-[#004985] to-[#0077D9] md:hidden w-full text-white py-2.5 font-medium flex items-center justify-center text-base leading-6 underline underline-offset-1 cursor-pointer"
        data-testid="earnRewardsBanner"
      >
        <StarIcon className="w-4 h-4 mr-2" />
        {i18n.t('rewards.button.earnRewards')}
      </button>
    );
  }

  if (variant === EARN_REWARDS_BUTTON_VARIANTS.MENU_MOBILE) {
    return (
      <button
        onClick={handleClick}
        className="md:hidden w-full py-2 px-4 text-white bg-white flex items-center justify-center"
        data-testid="earnRewardsButtonMobile"
      >
        <div className="w-6 h-6 bg-gradient-to-r from-[#114A7B] to-[#1F87E1] rounded-full flex items-center justify-center mr-2">
          <StarIcon className="w-3 h-3 text-white" />
        </div>
        {i18n.t('myRewards')}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="bg-gradient-to-r from-[#004985] to-[#0077D9] hidden md:flex text-white items-center text-sm font-medium rounded-3xl max-h-9 py-3 px-4 cursor-pointer"
      data-testid="earnRewardsButton"
    >
      <StarIcon className="w-3 h-3 mr-2" />
      {i18n.t('rewards.button.earnRewards')}
    </button>
  );
};
